import React from 'react';
import { Box, Typography } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import IntegrationInstructionsOutlinedIcon from '@mui/icons-material/IntegrationInstructionsOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import AddIcon from '@mui/icons-material/Add';
import { AI_AGENT_UI } from './tokens';

export type AgentQuickStartItem = {
  id: string;
  label: string;
  prompt: string;
  tint: string;
  icon: React.ReactNode;
};

export const DEFAULT_AGENT_QUICK_START: AgentQuickStartItem[] = [
  {
    id: 'create_select',
    label: 'Create or select project',
    prompt: 'create or select project',
    tint: 'rgba(245, 158, 11, 0.16)',
    icon: <FolderOutlinedIcon sx={{ fontSize: 20, color: '#d97706' }} />,
  },
  {
    id: 'import_swagger',
    label: 'Import Swagger / OpenAPI',
    prompt: 'import swagger',
    tint: 'rgba(59, 130, 246, 0.14)',
    icon: <IntegrationInstructionsOutlinedIcon sx={{ fontSize: 20, color: '#2563eb' }} />,
  },
  {
    id: 'create_environment',
    label: 'Create environment',
    prompt: 'create environment',
    tint: 'rgba(16, 185, 129, 0.14)',
    icon: <DnsOutlinedIcon sx={{ fontSize: 20, color: '#059669' }} />,
  },
  {
    id: 'execute_request',
    label: 'Execute request',
    prompt: 'execute request',
    tint: 'rgba(236, 72, 153, 0.14)',
    icon: <PlayArrowOutlinedIcon sx={{ fontSize: 20, color: '#db2777' }} />,
  },
];

type Props = {
  onSelect: (item: AgentQuickStartItem) => void;
  disabled?: boolean;
  items?: AgentQuickStartItem[];
};

/** Empty-state welcome + quick-start cards — AI Agent page only. */
export function AgentQuickStart({
  onSelect,
  disabled,
  items = DEFAULT_AGENT_QUICK_START,
}: Props) {
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: AI_AGENT_UI.contentMaxWidth,
        mx: 'auto',
        pt: { xs: 2, md: 4 },
        pb: 2,
        px: 1,
      }}
    >
      <Typography
        sx={{
          textAlign: 'center',
          fontSize: { xs: '1.35rem', md: AI_AGENT_UI.fontLg },
          fontWeight: 700,
          color: AI_AGENT_UI.text,
          letterSpacing: '-0.02em',
          mb: 1,
        }}
      >
        Welcome to AI Agent
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          fontSize: AI_AGENT_UI.font,
          color: AI_AGENT_UI.textSecondary,
          maxWidth: 420,
          mx: 'auto',
          mb: 3.5,
          lineHeight: 1.5,
        }}
      >
        Get started by describing a task — the agent can set up projects, import Swagger, configure
        environments, and run requests. Not sure where to start?
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1,
          maxWidth: 440,
          width: '100%',
          mx: 'auto',
        }}
      >
        {items.map((item) => (
          <Box
            key={item.id}
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => onSelect(item)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textAlign: 'left',
              border: `1px solid ${AI_AGENT_UI.border}`,
              bgcolor: AI_AGENT_UI.surface,
              borderRadius: `${AI_AGENT_UI.radius.lg}px`,
              px: 1.25,
              py: 1,
              minHeight: 52,
              cursor: disabled ? 'default' : 'pointer',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
              opacity: disabled ? 0.6 : 1,
              '&:hover': disabled
                ? undefined
                : {
                    borderColor: AI_AGENT_UI.borderStrong,
                    boxShadow: AI_AGENT_UI.shadow,
                  },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: `${AI_AGENT_UI.radius.sm}px`,
                bgcolor: item.tint,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              {item.icon}
            </Box>
            <Typography
              sx={{
                flex: 1,
                fontSize: AI_AGENT_UI.font,
                fontWeight: 600,
                color: AI_AGENT_UI.text,
                lineHeight: 1.35,
              }}
            >
              {item.label}
            </Typography>
            <AddIcon sx={{ fontSize: 18, color: AI_AGENT_UI.textSecondary, flexShrink: 0 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
